






















































































































import { Component, Watch } from 'vue-property-decorator';
import * as d3 from 'd3';
import reportDownloader from '@/lib/ReportDownloader';
import dateTimeRangeCalculator from '@/lib/DateTimeRangeCalculator';
import { ReportComponent, ReportAction } from './classes/ReportComponent';
import { DataTableHeader } from 'vuetify';
import { EnabledReportControls } from '../../../store/modules/reports';

type REOCOrderReportRawData = {
  service_category: string;
  dist_channel: string;
  order_priority: string;
  count_orders_created: number;
  count_orders_shipped: number;
  count_orders_not_filled_100: number;
  sla_met: number;
};

type REOCOrderReportData = {
  [orderPriority: string]: any;
  serviceCategory: string;
  distChannel: string;
};

@Component({
  name: 'REOCOrderReport',
})
export default class REOCOrderReport extends ReportComponent {
  public readonly enabledControls: EnabledReportControls = {
    search: true,
    dateTimeRange: true,
    distChannels: false,
    displayByContainers: false,
  };

  public readonly className = 'reoc-order-report';

  public readonly name = 'REOC Order Report';

  public readonly description = 'Summary of orders by priority, organized by area of service.';

  public actions: ReportAction[] = [
    {
      label: 'Export as CSV',
      value: 'export_csv',
      icon: 'mdi-file-delimited',
    },
    {
      label: 'Export Raw Data',
      value: 'export_raw_data',
      icon: 'mdi-download',
    },
    {
      label: 'Export as PDF',
      value: 'export_pdf',
      icon: 'mdi-file-delimited',
    },
  ];

  protected data: REOCOrderReportData[] = [];

  /**
   * Raw, original data pulled from API for this Report
   */
  private rawData: any[] = [];

  private headers: DataTableHeader[] = [];

  protected get tableBgColor() {
    return {
      background: (this.$vuetify.theme.dark) ? '#1E1E1E' : 'white',
    };
  }

  /**
   * Callback for date-range watcher; reloads data from server
   * with new query parameters
   */
  @Watch('dateTimeRange')
  protected async onControlChange() {
    this.$nextTick(() => this.debouncedInit());
  }

  public created() {
    this.toggleControls();
    this.init();
  }

  public execute(command: string): void {
    if (command === 'export_csv') {
      this.downloadCsvReport();
      return;
    }
    if (command === 'export_raw_data') {
      this.downloadCsvRawData();
      return;
    }
    if (command === 'export_pdf') {
      this.downloadPdfReport();
    }
  }

  protected pdfHeight() {
    return this.tableDimensions().height * 0.24;
  }

  protected pdfWidth() {
    return this.tableDimensions().width * 0.3;
  }

  protected async fetchData() {
    if (!this.dateTimeRange) throw Error('Unable to fetch data without `dateTimeRange`');
    const { startDateTime, endDateTime } = dateTimeRangeCalculator.calcDateTimeRange(this.dateTimeRange);
    const response = await this.reportService.api.communitySitesOrderPriority({
      authentication_token: this.userService.getActiveToken(),
      report_params: {
        startDate: startDateTime,
        endDate: endDateTime,
      },
    });
    this.rawData = response;
    return this.formatData(response);
  }

  /**
   * Get column style
   */
  protected getColumnStyle(colKey: any, colIndex: any) {
    if (colKey === '_total') {
      return {
        'yellow-lighten-5-bg-only': !this.$vuetify.theme.dark,
        'yellow-darken-3-bg-only': this.$vuetify.theme.dark,
        'text-right': true,
        'font-weight-bold': true,
      };
    }
    if (colIndex % 2) {
      return {
        'text-right': true,
      };
    }
    return {
      'blue-lighten-5-bg-only': !this.$vuetify.theme.dark,
      'primary-darken-3-bg-only': this.$vuetify.theme.dark,
      'text-right': true,
    };
  }

  /**
   * Load dynamic headers for Report
   */
  private loadHeaders(orderPriorities: string[]) {
    this.headers =[
      {
        text: 'Area of Service',
        value: 'serviceCategory',
      },
      // {
      //   text: 'Distribution Channel',
      //   value: 'distChannel',
      // },
    ];
    // add order priorities as header columns
    orderPriorities.forEach((priority) => {
      this.headers.push({
        text: priority,
        value: priority,
        width: 3,
      });
    });
    this.headers.push({
      text: 'Total',
      value: '_total',
      width: 3,
    });
  }

  /**
   * Format data for Report before rendering in table
   */
  private formatData(data: REOCOrderReportRawData[]) {
    const nestedReportData = d3
      .nest()
      .key((d: any) => d.service_category).sortKeys(d3.ascending)
      .key((d: any) => d.dist_channel)
      .key((d: any) => d.order_priority)
      .rollup((leaves: any) => leaves[0])
      .entries(data);

    // get unique set of order priorities
    const orderPriorities: string[] = nestedReportData[nestedReportData.length - 1].values[0].values
      .map((priority: any) => priority.key);

    this.loadHeaders(orderPriorities);

    /*
    pivot report data to summarize order priorty per
    service category group and distribution channel

    structure generated:
    [
      {
        serviceCategory: [string | null],
        distChannel: [string | null],
        EMERGENCY: { count_orders_shipped: [number], ...},
        RUSH: { count_orders_shipped: [number], ...},
        ...(additional order priorities)
      },
      ...(additional service categories)
    ]

    note: empty string in distChannel columns represent subtotal rows for service category

    example:
       { serviceCategory: 'TEST', distChannel: '' } => represents the subtotal row for TEST category
    */
    const flatReportData = nestedReportData.flatMap((serviceCategory) => {
      return serviceCategory.values.map((distChannel: any) => {
        const categoryRow: REOCOrderReportData = {
          serviceCategory: serviceCategory.key,
          distChannel: distChannel.key,
          _total: {
            count_orders_created: d3.sum(distChannel.values, (p: any) => p.value.count_orders_created),
            count_orders_shipped: d3.sum(distChannel.values, (p: any) => p.value.count_orders_shipped),
            count_orders_not_filled_100: d3.sum(distChannel.values, (p: any) => p.value.count_orders_not_filled_100),
            sla_met: d3.sum(distChannel.values, (p: any) => p.value.sla_met),
          },
        };
        orderPriorities.forEach((priority) => {
          const priorityData = distChannel.values.find((o: any) => o.key === priority);
          if (priorityData) {
            categoryRow[priority] = {
              count_orders_created: priorityData.value.count_orders_created,
              count_orders_shipped: priorityData.value.count_orders_shipped,
              count_orders_not_filled_100: priorityData.value.count_orders_not_filled_100,
              sla_met: priorityData.value.sla_met,
            };
          } else {
            categoryRow[priority] = {
              count_orders_created: 0,
              count_orders_shipped: 0,
              count_orders_not_filled_100: 0,
              sla_met: 0,
            };
          }
        });
        return categoryRow;
      });
    });
    return flatReportData;
  }

  /**
   * Download CSV file of Report data
   */
  private downloadCsvReport() {
    if (!this.dateTimeRange) throw Error('Unable to download CSV without `dateTimeRange`');
    const dateRangeText = `${this.dateTimeRange.startDate} - ${this.dateTimeRange.endDate}`;
    const filename = `${this.name} [${dateRangeText}]`;
    const headers = ['Area of Service'];
    const labels = [''];
    this.headers.slice(1).forEach((header) => {
      headers.push(header.text);
      headers.push('');
      headers.push('');

      labels.push('Created');
      labels.push('Shipped');
      labels.push('Not 100% Filled');
      // headers.push(`${header.text} Orders SLA Met`);
    });
    const data = [headers, labels].concat(this.data.map((row, i) => {
      const out = [];
      if (!row.distChannel) {
        out.push(row.serviceCategory);
      } else {
        out.push(row.distChannel);
      }
      if (i == this.data.length - 1) out[0] = 'Grand Total';
      this.headers.slice(1).forEach((header) => {
        out.push(row[header.value].count_orders_created);
        out.push(row[header.value].count_orders_shipped);
        out.push(row[header.value].count_orders_not_filled_100);
        // out.push(row[header.value].sla_met);
      });
      return out;
    }));
    reportDownloader.downloadCsv(filename, data);
  }

  /**
   * Download Csv Raw Data
   */
  private downloadCsvRawData() {
    const { startDate, endDate } = this.dateTimeRange;
    const filename = `${this.name} [${startDate} - ${endDate}] (raw)`;
    const headers = [
      'service_category',
      'dist_channel',
      'order_priority',
      'count_orders_created',
      'count_orders_shipped',
      'count_orders_not_filled_100',
      // 'sla_met',
    ];
    const data = [headers].concat(this.rawData.map((row) => [
      row.service_category,
      row.dist_channel,
      row.order_priority,
      row.count_orders_created,
      row.count_orders_shipped,
      row.count_orders_not_filled_100,
      // row.sla_met,
    ]));
    reportDownloader.downloadCsv(filename, data);
  }
}
